/**
 * motionSystem — the single source of truth for Command Center motion.
 *
 * Surfaces COMPOSE these primitives; they must never inline their own durations,
 * easings, scale values, or accent colors. If a primitive doesn't offer what a
 * surface needs, extend it HERE so the change propagates everywhere.
 *
 * Rules baked in:
 *  - Interactive motion is capped ~300ms; only data motion runs longer.
 *  - Three easings only. Overshoot is reserved for the Bolt FAB hero moment — never
 *    navigation, data, or utility controls.
 *  - Orange #FF6A00 is the only animated accent (glow/ring/border), never a fill.
 *  - prefers-reduced-motion is a hard gate (see <Pressable> + motion.css).
 */
import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import './motion.css';

// --- Easings (the only three; overshoot is reserved) ---
export const easings = {
  standard: [0.4, 0, 0.2, 1], // entrances, exits, position/scale, data fills, selection slide
  snappy: [0.22, 1, 0.3, 1], // press feedback + one-shot rings
  overshoot: [0.34, 1.56, 0.64, 1], // RESERVED: Bolt FAB hero lift + celebration rings only
};

// --- Durations (seconds) ---
export const durations = {
  pressIn: 0.08,
  hover: 0.18,
  entrance: 0.3,
  overlayIn: 0.28,
  overlayOut: 0.2,
  selection: 0.28,
};

// --- Pressable: unified hover + press affordance for buttons/cards/rows ---
export const pressableVariants = {
  rest: { scale: 1, y: 0 },
  hover: { scale: 1.02, transition: { duration: durations.hover, ease: easings.standard } },
  tap: { scale: 0.97, transition: { duration: durations.pressIn, ease: easings.snappy } },
};

// Card variant — lifts instead of scaling (better for larger surfaces).
export const cardLiftVariants = {
  rest: { y: 0, scale: 1 },
  hover: { y: -4, transition: { duration: 0.22, ease: easings.standard } },
  tap: { scale: 0.99, transition: { duration: durations.pressIn, ease: easings.snappy } },
};

// --- entranceStagger: views "compose in" rather than popping as a block ---
export const entranceContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05, delayChildren: 0.08 } },
};

export const entranceItem = {
  hidden: { opacity: 0, y: 14, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: durations.entrance, ease: easings.standard },
  },
};

// --- overlayPanel: one grammar for modals / slide-overs / dropdowns ---
// CRITICAL: animate only transform/opacity, never width/height (keeps drag-resize instant).
export const overlayBackdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: durations.overlayOut, ease: easings.standard } },
  exit: { opacity: 0, transition: { duration: 0.15, ease: easings.snappy } },
};

export const overlayPanelVariants = {
  hidden: { opacity: 0, scale: 0.96, y: 12 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: durations.overlayIn, ease: easings.standard },
  },
  exit: { opacity: 0, scale: 0.98, y: 8, transition: { duration: durations.overlayOut, ease: easings.snappy } },
};

// --- selectionIndicator: sliding orange pill/underline (framer layoutId transition) ---
// Timing curve, NOT a spring — must track like a physical cursor, no overshoot.
export const selectionIndicatorTransition = { duration: durations.selection, ease: easings.standard };

/**
 * <Pressable> — the most-reused web primitive. A motion element that applies the
 * calibrated hover/press scale and (optionally) the orange hover glow. Honors
 * reduced-motion: drops scale/lift, keeps the static glow shadow.
 *
 * Usage: <Pressable as="button" glow onClick={...}>…</Pressable>
 *        <Pressable as="div" lift>…</Pressable>   // card lift instead of scale
 */
export function Pressable({ as = 'button', glow = false, lift = false, className = '', children, ...rest }) {
  const reduce = useReducedMotion();
  const MotionTag = motion[as] || motion.button;
  const variants = lift ? cardLiftVariants : pressableVariants;
  return (
    <MotionTag
      className={`${glow ? 'vh-pressable-glow' : ''} ${className}`.trim()}
      variants={reduce ? undefined : variants}
      initial="rest"
      animate="rest"
      whileHover={reduce ? undefined : 'hover'}
      whileTap={reduce ? undefined : 'tap'}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

/**
 * <PressRing> — one-shot orange ring on a committed press. Place inside a
 * position:relative host. Replay by bumping `trigger` (e.g. a click counter);
 * the changing key remounts the element so the CSS animation restarts.
 * Use sparingly — primary/commit actions only, never rapid/repeated controls.
 */
export function PressRing({ trigger }) {
  if (!trigger) return null;
  return <span key={trigger} className="vh-press-ring" aria-hidden="true" />;
}
